import type { DelegationIdentity } from "@icp-sdk/core/identity";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("lib/services/odin-fun-auth");

export interface OdinFunAuthRequest {
	publickey: string;
	delegation: string;
	timestamp: string;
	signature: string;
	referrer: string;
}

export interface OdinFunAuthResponse {
	token: string;
}

export interface OdinFunAuthError {
	error: string;
	message?: string;
}

/**
 * odin.fun 授权服务
 * 处理登录成功后的 odin.fun 平台授权
 */
export class OdinFunAuthService {
	private static readonly API_BASE_URL = "https://api.odin.fun/v1";
	private static readonly AUTH_ENDPOINT = "/auth";
	private static readonly REFERRER_CODE = "xk5vhwq5l2";
	private static readonly TOKEN_STORAGE_KEY = "odinfun_authtoken";

	/**
	 * 获取 odin.fun 授权
	 * @param identity - 用户的 DelegationIdentity
	 * @returns Promise<string> - 返回授权 token
	 */
	static async getOdinFunAuth(identity: DelegationIdentity): Promise<string> {
		try {
			const now = Date.now();
			const timestamp = now.toString();

			// ✅ 根据参考代码修复：正确的请求格式
			const isDelegationIdentityFlag = true; // 我们的 identity 是 DelegationIdentity

			// 1. 正确的签名方式：使用 TextEncoder
		const signatureData = await identity.sign(
			new TextEncoder().encode(timestamp),
		);
			const signature = Buffer.from(signatureData).toString("base64");

			// 2. 正确的委托格式：使用 toJSON() 方法
			const delegation = isDelegationIdentityFlag
				? JSON.stringify(identity.getDelegation().toJSON())
				: undefined;

			// 3. 正确的公钥：仅在非 DelegationIdentity 时发送
			const publickey = isDelegationIdentityFlag
				? undefined
				: Buffer.from(identity.getPublicKey().toDer()).toString("base64");

			uiLog.info("🔍 使用参考代码的正确格式:", {
				hasDelegation: !!delegation,
				hasPublickey: !!publickey,
				signatureLength: signature.length,
				delegationLength: delegation?.length || 0,
			});

			// ✅ 使用参考代码的正确请求格式
			const requestPayload: any = {
				timestamp,
				signature,
				referrer: OdinFunAuthService.REFERRER_CODE,
			};

			// 根据参考代码逻辑：
			// - DelegationIdentity: 发送 delegation，不发送 publickey
			// - 其他类型: 发送 publickey，不发送 delegation
			if (isDelegationIdentityFlag) {
				requestPayload.delegation = delegation;
			} else {
				requestPayload.publickey = publickey;
			}

			uiLog.info("📤 发送 odin.fun 授权请求（参考代码格式）:", {
				timestamp,
				referrer: OdinFunAuthService.REFERRER_CODE,
				hasDelegation: !!requestPayload.delegation,
				hasPublickey: !!requestPayload.publickey,
				delegationLength: requestPayload.delegation?.length || 0,
				signatureLength: signature.length,
			});

			const response = await fetch(
				`${OdinFunAuthService.API_BASE_URL}${OdinFunAuthService.AUTH_ENDPOINT}`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify(requestPayload),
				},
			);

			if (!response.ok) {
				let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
				try {
					const errorData: OdinFunAuthError = await response.json();
					errorMessage = errorData.message || errorData.error || errorMessage;
				} catch (e) {
					// 如果无法解析错误响应，使用默认错误信息
				}
				throw new Error(`odin.fun 授权请求失败: ${errorMessage}`);
			}

			const authResult: OdinFunAuthResponse = await response.json();

			if (!authResult.token) {
				throw new Error("odin.fun 返回的响应中缺少 token");
			}

			uiLog.info("✅ odin.fun 授权获取成功！");

			// 保存 token 到本地存储
			OdinFunAuthService.saveToken(authResult.token);

			return authResult.token;
		} catch (error) {
			uiLog.error("odin.fun 授权失败:", error);
			throw error;
		}
	}

	/**
	 * 保存授权 token 到本地存储
	 * @param token - 授权 token
	 */
	static saveToken(token: string): void {
		try {
			localStorage.setItem(OdinFunAuthService.TOKEN_STORAGE_KEY, token);
			// 同时保存时间戳，用于后续检查token有效性
			localStorage.setItem(
				`${OdinFunAuthService.TOKEN_STORAGE_KEY}_timestamp`,
				Date.now().toString(),
			);
		} catch (error) {
			uiLog.error("保存 odin.fun token 失败:", error);
		}
	}

	/**
	 * 从本地存储获取授权 token
	 * @returns string | null - 返回 token 或 null
	 */
	static getToken(): string | null {
		try {
			return localStorage.getItem(OdinFunAuthService.TOKEN_STORAGE_KEY);
		} catch (error) {
			uiLog.error("获取 odin.fun token 失败:", error);
			return null;
		}
	}

	/**
	 * 检查 token 是否存在
	 * @returns boolean
	 */
	static hasToken(): boolean {
		return OdinFunAuthService.getToken() !== null;
	}

	/**
	 * 清除本地存储的 token
	 */
	static clearToken(): void {
		try {
			localStorage.removeItem(OdinFunAuthService.TOKEN_STORAGE_KEY);
			localStorage.removeItem(
				`${OdinFunAuthService.TOKEN_STORAGE_KEY}_timestamp`,
			);
		} catch (error) {
			uiLog.error("清除 odin.fun token 失败:", error);
		}
	}

	/**
	 * 获取 token 保存时间戳
	 * @returns number | null
	 */
	static getTokenTimestamp(): number | null {
		try {
			const timestamp = localStorage.getItem(
				`${OdinFunAuthService.TOKEN_STORAGE_KEY}_timestamp`,
			);
			return timestamp ? parseInt(timestamp) : null;
		} catch (error) {
			uiLog.error("获取 odin.fun token 时间戳失败:", error);
			return null;
		}
	}

	/**
	 * 检查 token 是否过期（可选功能，需要根据实际需求调整过期时间）
	 * @param maxAgeHours - token 最大有效时间（小时），默认24小时
	 * @returns boolean
	 */
	static isTokenExpired(maxAgeHours: number = 24): boolean {
		const timestamp = OdinFunAuthService.getTokenTimestamp();
		if (!timestamp) return true;

		const maxAgeMs = maxAgeHours * 60 * 60 * 1000;
		return Date.now() - timestamp > maxAgeMs;
	}

	/**
	 * 获取有效的 token（如果过期则返回 null）
	 * @param maxAgeHours - token 最大有效时间（小时），默认24小时
	 * @returns string | null
	 */
	static getValidToken(maxAgeHours: number = 24): string | null {
		if (OdinFunAuthService.isTokenExpired(maxAgeHours)) {
			OdinFunAuthService.clearToken();
			return null;
		}
		return OdinFunAuthService.getToken();
	}
}
